import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const OPEN_PATHS = ["/login", "/forgot-password", "/reset-password"];

function MaintenanceScreen({ message, onLogout }) {
  return (
    <div className="min-h-screen flex items-center px-6 sm:px-16 relative overflow-hidden" style={{ backgroundColor: "#050505" }} data-testid="maintenance-screen">
      <div className="absolute inset-0 opacity-20 bg-cover bg-center grayscale" style={{ backgroundImage: "url(/images/hero-texture.jpg)" }} />
      <div className="relative max-w-2xl">
        <img src="/images/logo.jpg" alt="GameBoxMods.com" className="h-12 mb-12" />
        <div className="flex items-center gap-2 mb-4">
          <Wrench className="h-4 w-4 text-[#39FF14] animate-pulse" strokeWidth={1.5} />
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Down for maintenance</p>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
          Back <span className="text-[#39FF14]">soon.</span>
        </h1>
        <p className="text-[#A1A1A1] text-base leading-relaxed mb-10" data-testid="maintenance-message">{message}</p>
        {onLogout ? (
          <button onClick={onLogout} className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1A1] hover:text-[#39FF14] transition-colors" data-testid="maintenance-logout">
            Log out
          </button>
        ) : (
          <Link to="/login" className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1A1] hover:text-[#39FF14] transition-colors" data-testid="maintenance-admin-login">
            Admin login
          </Link>
        )}
      </div>
    </div>
  );
}

function AdminBanner() {
  return (
    <div className="bg-[#39FF14] text-[#050505] text-center font-mono text-[11px] uppercase tracking-[0.15em] font-bold py-2 px-4" data-testid="maintenance-admin-banner">
      Maintenance mode is ON — customers can't see the store.{" "}
      <Link to="/admin/site-content" className="underline">Turn it off</Link>
    </div>
  );
}

export function useMaintenance() {
  const [state, setState] = useState(null);
  const reload = useCallback(() => {
    axios.get(`${API}/api/maintenance`).then(({ data }) => setState(data)).catch(() => setState({ enabled: false }));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    window.addEventListener("maintenance-changed", reload);
    return () => window.removeEventListener("maintenance-changed", reload);
  }, [reload]);
  return state;
}

export default function MaintenanceGate({ children }) {
  const { user, loading, logout } = useAuth();
  const { pathname } = useLocation();
  const state = useMaintenance();

  if (!state || loading) return <div className="min-h-screen" style={{ backgroundColor: "#050505" }} />;
  const isAdmin = user?.role === "admin";
  if (state.enabled && !isAdmin && !OPEN_PATHS.includes(pathname)) {
    return <MaintenanceScreen message={state.message} onLogout={user ? logout : null} />;
  }
  return <>{state.enabled && isAdmin && <AdminBanner />}{children}</>;
}
